/**
 * ConsentManager Component
 * Handles GDPR consent collection and management
 */

import React, { useState, useEffect } from 'react';
import { Shield, Info, Check, X, Settings } from 'lucide-react';
import { GDPR } from '../../utils/dataPrivacy';
import { useTheme, getThemeStyles } from '../../contexts/ThemeContext';

const ConsentManager = ({ 
  userId, 
  isVisible = false, 
  onClose, 
  onConsentChange,
  requiredForAccess = false 
}) => {
  const { isDarkMode } = useTheme();
  const themeStyles = getThemeStyles(isDarkMode);
  
  const [consentStatus, setConsentStatus] = useState({
    audit_logs: false,
    analytics: false,
    marketing: false
  });
  
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (userId && isVisible) {
      // Check existing consent status
      const auditConsent = GDPR.hasConsent(userId, 'audit_logs');
      const analyticsConsent = GDPR.hasConsent(userId, 'analytics');
      const marketingConsent = GDPR.hasConsent(userId, 'marketing');
      
      setConsentStatus({
        audit_logs: auditConsent,
        analytics: analyticsConsent,
        marketing: marketingConsent
      });
    }
  }, [userId, isVisible]);

  const handleConsentChange = (purposeType, granted) => {
    setConsentStatus(prev => ({
      ...prev,
      [purposeType]: granted
    }));
  };

  const handleSaveConsent = async () => {
    setLoading(true);
    
    try {
      // Record consent for each purpose
      Object.entries(consentStatus).forEach(([purposeType, granted]) => {
        if (granted) {
          GDPR.recordConsent(userId, purposeType, true);
        } else {
          GDPR.revokeConsent(userId, purposeType);
        }
      });
      
      // Notify parent component
      if (onConsentChange) {
        onConsentChange(consentStatus);
      }
      
      if (!requiredForAccess || consentStatus.audit_logs) {
        onClose();
      }
    } catch (error) {
      console.error('Error saving consent:', error);
    } finally {
      setLoading(false);
    }
  };

  const consentPurposes = [
    {
      id: 'audit_logs',
      title: 'Audit Log Access',
      description: 'Allow processing of your data for audit log viewing and security monitoring.',
      required: requiredForAccess,
      details: `This consent allows you to:
        • View audit logs for your actions
        • Access security dashboard information  
        • Receive security notifications
        
        We collect and process:
        • User action timestamps
        • IP addresses (partially masked)
        • User agent information
        • Action details and outcomes`
    },
    {
      id: 'analytics',
      title: 'Usage Analytics',
      description: 'Help us improve the application by analyzing usage patterns.',
      required: false,
      details: `This consent allows us to:
        • Analyze feature usage patterns
        • Identify performance bottlenecks
        • Improve user experience
        
        We collect and process:
        • Anonymized usage statistics
        • Performance metrics
        • Error reports (without personal data)`
    },
    {
      id: 'marketing',
      title: 'Product Updates',
      description: 'Receive updates about new features and improvements.',
      required: false,
      details: `This consent allows us to:
        • Send product update notifications
        • Share feature announcements
        • Provide training resources
        
        We will not:
        • Share your data with third parties
        • Send marketing from other companies
        • Sell your information`
    }
  ];

  if (!isVisible) return null;

  const hasRequiredConsent = !requiredForAccess || consentStatus.audit_logs;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`${themeStyles.bg.primary} rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden`}>
        {/* Header */}
        <div className={`${themeStyles.bg.secondary} px-6 py-4 border-b ${themeStyles.border.primary}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Shield className="h-6 w-6 text-blue-600" />
              <h2 className={`text-xl font-semibold ${themeStyles.text.primary}`}>
                Data Privacy Consent
              </h2>
            </div>
            {!requiredForAccess && (
              <button
                onClick={onClose}
                className={`${themeStyles.text.secondary} hover:${themeStyles.text.primary}`}
              >
                <X className="h-6 w-6" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6">
            {/* Introduction */}
            <div className="mb-6">
              <p className={`${themeStyles.text.secondary} mb-4`}>
                In compliance with GDPR and data protection regulations, we need your consent 
                to process your personal data for specific purposes. You can withdraw your 
                consent at any time.
              </p>
              
              {requiredForAccess && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
                  <div className="flex items-start space-x-3">
                    <Info className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-yellow-800 dark:text-yellow-200">
                        Required Consent
                      </h4>
                      <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                        Audit log access consent is required to use this feature.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Consent Options */}
            <div className="space-y-4">
              {consentPurposes.map((purpose) => (
                <div
                  key={purpose.id}
                  className={`border ${themeStyles.border.primary} rounded-lg p-4`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className={`font-medium ${themeStyles.text.primary}`}>
                          {purpose.title}
                        </h3>
                        {purpose.required && (
                          <span className="text-xs bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 px-2 py-1 rounded">
                            Required
                          </span>
                        )}
                      </div>
                      <p className={`text-sm ${themeStyles.text.secondary} mb-3`}>
                        {purpose.description}
                      </p>
                      
                      {/* Details Toggle */}
                      <button
                        onClick={() => setShowDetails(showDetails === purpose.id ? null : purpose.id)}
                        className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center space-x-1"
                      >
                        <Settings className="h-4 w-4" />
                        <span>
                          {showDetails === purpose.id ? 'Hide Details' : 'Show Details'}
                        </span>
                      </button>
                      
                      {/* Details Content */}
                      {showDetails === purpose.id && (
                        <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded text-sm">
                          <pre className={`whitespace-pre-wrap ${themeStyles.text.secondary}`}>
                            {purpose.details}
                          </pre>
                        </div>
                      )}
                    </div>
                    
                    {/* Toggle Switch */}
                    <div className="ml-4">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={consentStatus[purpose.id]}
                          onChange={(e) => handleConsentChange(purpose.id, e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer Info */}
            <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <h4 className={`font-medium ${themeStyles.text.primary} mb-2`}>
                Your Rights
              </h4>
              <ul className={`text-sm ${themeStyles.text.secondary} space-y-1`}>
                <li>• You can withdraw consent at any time</li>
                <li>• You can request a copy of your data</li>
                <li>• You can request deletion of your data</li>
                <li>• You can correct inaccurate data</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`${themeStyles.bg.secondary} px-6 py-4 border-t ${themeStyles.border.primary}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {hasRequiredConsent ? (
                <Check className="h-5 w-5 text-green-600" />
              ) : (
                <X className="h-5 w-5 text-red-600" />
              )}
              <span className={`text-sm ${themeStyles.text.secondary}`}>
                {hasRequiredConsent 
                  ? 'Required consents granted' 
                  : 'Required consent needed to proceed'
                }
              </span>
            </div>
            
            <div className="flex space-x-3">
              {!requiredForAccess && (
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={handleSaveConsent}
                disabled={loading || (requiredForAccess && !hasRequiredConsent)}
                className={`px-4 py-2 rounded-md font-medium ${
                  loading || (requiredForAccess && !hasRequiredConsent)
                    ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {loading ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentManager;
